import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatRoas } from "@/lib/formatters";
import type { QualityScoreRow } from "./types";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

const RANKING_BADGE: Record<string, { label: string; className: string }> = {
  ABOVE_AVERAGE: { label: "Above Avg", className: "bg-emerald-50 text-emerald-700" },
  AVERAGE: { label: "Average", className: "bg-gray-100 text-gray-600" },
  BELOW_AVERAGE: { label: "Below Avg", className: "bg-red-50 text-red-700" },
};

export function QualityScoresTable({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<QualityScoreRow[]>({
    queryKey: ["dashboard-quality-scores", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate, limit: "10" });
      const res = await fetch(getUrl("quality-scores", params));
      if (!res.ok) throw new Error("Failed to fetch quality scores");
      return res.json();
    },
  });

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Quality Scores" widgetType="table" isEditMode={isEditMode} isLoading={isLoading} error={error instanceof Error ? error.message : null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-quality">No quality score data available</div>
      ) : (
        <div className="overflow-auto h-full" data-testid="table-quality">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white border-b border-gray-100">
              <tr>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3 w-8">#</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Ad</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Quality</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Engagement</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Conversion</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Score</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((ad, idx) => {
                const scorePct = Math.round((ad.qualityScore / 9) * 100);
                const qBadge = RANKING_BADGE[ad.qualityRanking] || RANKING_BADGE.AVERAGE;
                const eBadge = RANKING_BADGE[ad.engagementRanking] || RANKING_BADGE.AVERAGE;
                const cBadge = RANKING_BADGE[ad.conversionRanking] || RANKING_BADGE.AVERAGE;
                return (
                  <tr key={ad.adId} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 px-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        idx < 3 ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-500"
                      }`}>
                        {idx + 1}
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        {ad.thumbnailUrl ? (
                          <img
                            src={ad.thumbnailUrl}
                            alt={ad.adName}
                            className="w-8 h-8 rounded object-cover flex-shrink-0 bg-gray-100"
                            data-testid={`quality-thumb-${ad.adId}`}
                          />
                        ) : (
                          <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-[8px] text-gray-400 uppercase">{ad.format}</span>
                          </div>
                        )}
                        <span className="font-medium text-gray-900 max-w-[140px] truncate">{ad.adName}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${qBadge.className}`}>{qBadge.label}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${eBadge.className}`}>{eBadge.label}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${cBadge.className}`}>{cBadge.label}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${scorePct}%` }} />
                        </div>
                        <span className="text-[10px] font-medium text-gray-600">{ad.qualityScore}/9</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 font-medium text-gray-900">{formatRoas(ad.roas)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardWidget>
  );
}
